import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

/**
 * v1.2 PR-B.1 — Service-worker update prompt.
 *
 * The PWA service worker (public/sw.js) is registered with
 * `updateViaCache: 'none'` in index.js, so the browser always re-checks for
 * a new SW on full reload. When a new SW is installed but still in the
 * `waiting` state (because the current page hasn't released it), we surface
 * a single in-app toast prompting the user to refresh.
 *
 * On confirm:
 *   1. postMessage({type:'SKIP_WAITING'}) to the waiting SW
 *   2. listen for `controllerchange` and full-reload the page
 *
 * Idempotent — toast shows at most once per session.
 */
let _toastShown = false;

export default function SwUpdater() {
  const [, setReg] = useState(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let cancelled = false;

    const onControllerChange = () => {
      // The new SW has taken control — reload so the page picks up the new chunks.
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration || cancelled) return;
      setReg(registration);

      const promptForUpdate = (waiting) => {
        if (_toastShown) return;
        _toastShown = true;
        toast.message("New version available", {
          description: "Refresh to load the latest build.",
          duration: 30_000,
          action: {
            label: "Refresh",
            onClick: () => {
              try { waiting.postMessage({ type: "SKIP_WAITING" }); }
              catch { window.location.reload(); }
            },
          },
          icon: <RefreshCw className="w-4 h-4" />,
        });
      };

      if (registration.waiting) {
        promptForUpdate(registration.waiting);
      }

      registration.addEventListener("updatefound", () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          // Only prompt if the new SW becomes "installed" AND there's already
          // an active controller (i.e. this is an update, not the first install).
          if (installing.state === "installed" && navigator.serviceWorker.controller && registration.waiting) {
            promptForUpdate(registration.waiting);
          }
        });
      });

      // Poll for updates once an hour while the tab is open.
      const pollId = setInterval(() => {
        try { registration.update(); } catch { /* ignore */ }
      }, 60 * 60 * 1000);

      return () => clearInterval(pollId);
    });

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, []);

  return null;
}
